import { NewApiBaseUrl } from '../consts/new-api-base-url'
import { unwrapApiPayload } from './crash-csv-import'

const getApiRoot = () => String(NewApiBaseUrl).replace(/\/$/, '')

/**
 * @param {unknown} data
 * @returns {{ responseCode?: number, responseMessage?: string, responseData?: unknown }|null}
 */
function unwrapEnvelope(data) {
  if (data == null || typeof data !== 'object') return null
  return /** @type {{ responseCode?: number, responseMessage?: string, responseData?: unknown }} */ (data)
}

/**
 * @param {unknown} data
 * @returns {string}
 */
export function getApiErrorMessage(data) {
  const envelope = unwrapEnvelope(data)
  if (envelope?.responseMessage) return envelope.responseMessage
  if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
    return data.message
  }
  return 'Request failed'
}

/**
 * @param {unknown} data
 * @returns {boolean}
 */
export function isApiSuccess(data) {
  const envelope = unwrapEnvelope(data)
  if (envelope && typeof envelope.responseCode === 'number') {
    return envelope.responseCode === 0
  }
  return true
}

/**
 * @param {string} token
 * @returns {Promise<{ status: number, body: unknown }>}
 */
export async function fetchConfigIconsList(token) {
  const res = await fetch(`${getApiRoot()}/ConfigIcons`, {
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
  const body = await res.json().catch(() => ({}))
  return { status: res.status, body }
}

/**
 * @param {unknown} data
 * @returns {string[]}
 */
export function parseConfigIconsList(data) {
  const inner = unwrapApiPayload(data)
  if (!inner || !Array.isArray(inner.icons)) return []
  return inner.icons.map((item) => String(item)).filter(Boolean)
}

/**
 * @param {unknown} data
 * @returns {string[]}
 */
export function parseConfigIconsDefaultIcons(data) {
  const inner = unwrapApiPayload(data)
  if (!inner || !Array.isArray(inner.defaultIcons)) return []
  return inner.defaultIcons.map((item) => String(item)).filter(Boolean)
}

/**
 * @param {string} token
 * @param {File} file
 * @param {string} symbolicName
 * @returns {Promise<{ status: number, body: unknown }>}
 */
export async function uploadConfigIcon(token, file, symbolicName) {
  const formData = new FormData()
  formData.append('svgFile', file)
  formData.append('symbolicName', symbolicName)

  const res = await fetch(`${getApiRoot()}/ConfigIcons`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  })
  const body = await res.json().catch(() => ({}))
  return { status: res.status, body }
}

/**
 * @param {string} token
 * @param {string} filename
 * @returns {Promise<{ status: number, body: unknown }>}
 */
export async function fetchConfigIconUsage(token, filename) {
  const safe = encodeURIComponent(filename)
  const res = await fetch(`${getApiRoot()}/ConfigIcons/${safe}/usage`, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  const body = await res.json().catch(() => ({}))
  return { status: res.status, body }
}

/**
 * @param {unknown} data
 * @returns {number}
 */
export function parseConfigIconUsage(data) {
  const inner = unwrapApiPayload(data)
  if (!inner || typeof inner.usageCount !== 'number') return 0
  return inner.usageCount
}

/**
 * @param {string} token
 * @param {string} filename
 * @param {string} newName
 * @returns {Promise<{ status: number, body: unknown }>}
 */
export async function renameConfigIcon(token, filename, newName) {
  const safe = encodeURIComponent(filename)
  const res = await fetch(`${getApiRoot()}/ConfigIcons/${safe}`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ newName }),
  })
  const body = await res.json().catch(() => ({}))
  return { status: res.status, body }
}

/**
 * @param {string} token
 * @param {string} filename
 * @returns {Promise<{ status: number, body: unknown }>}
 */
export async function deleteConfigIcon(token, filename) {
  const safe = encodeURIComponent(filename)
  const res = await fetch(`${getApiRoot()}/ConfigIcons/${safe}`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  const body = await res.json().catch(() => ({}))
  return { status: res.status, body }
}

/**
 * @param {unknown} data
 * @returns {string|null}
 */
export function parseUploadFilename(data) {
  const inner = unwrapApiPayload(data)
  if (!inner || typeof inner.filename !== 'string') return null
  return inner.filename
}

/**
 * @param {unknown} data
 * @returns {string|null}
 */
export function parseRenameFilename(data) {
  const inner = unwrapApiPayload(data)
  if (!inner || typeof inner.newFilename !== 'string') return null
  return inner.newFilename
}
